import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { fetchTraineesByManagerId, fetchAllActiveTrainees, createBatch } from '../../../api_service';

const CreateBatchModal = ({ managerId, onClose, onCreated }) => {
  const [form, setForm] = useState({
    name: '',
    description: '',
  });
  const [trainees, setTrainees] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const roleName = sessionStorage.getItem("roleName");
        const privileged = ["CEO","CTO","HR","PM","TL","MANAGER"];
        const res = privileged.includes(roleName) ? await fetchAllActiveTrainees() : await fetchTraineesByManagerId(managerId);
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        setTrainees(list);
      } catch {
        setTrainees([]);
      }
    };
    load();
  }, [managerId]);

  const toggleTrainee = (userid) => {
    setSelectedIds(prev =>
      prev.includes(userid) ? prev.filter(x => x !== userid) : [...prev, userid]
    );
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) { setError('Batch name is required'); return; }
    setLoading(true);
    setError('');
    try {
      await createBatch(managerId, {
        name: form.name,
        description: form.description,
        traineeIds: selectedIds,
      });
      if (onCreated) await onCreated();
      onClose();
    } catch {
      setError('Failed to create batch. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">Create New Batch</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-2xl leading-none">&times;</button>
        </div>

        <div className="overflow-y-auto p-6 space-y-4 flex-1">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Batch Name *</label>
            <Input
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. June 2026 Onboarding Cohort"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Description</label>
            <textarea
              className="w-full border border-border rounded-lg p-3 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              rows={4}
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Describe the batch, its focus area, or schedule..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Add Trainees <span className="text-muted-foreground font-normal">({selectedIds.length} selected)</span>
            </label>
            <div className="border border-border rounded-lg divide-y divide-border max-h-48 overflow-y-auto">
              {trainees.length === 0 ? (
                <p className="p-3 text-sm text-muted-foreground">No trainees found</p>
              ) : (
                trainees.map(t => (
                  <label key={t.userid} className="flex items-center gap-3 p-3 hover:bg-muted/50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(t.userid)}
                      onChange={() => toggleTrainee(t.userid)}
                      className="accent-primary"
                    />
                    <span className="text-sm text-foreground">{t.firstname} {t.lastname}</span>
                    <span className="text-xs text-muted-foreground ml-auto">{t.emailid}</span>
                  </label>
                ))
              )}
            </div>
          </div>

          {error && <p className="text-sm text-error">{error}</p>}
        </div>

        <div className="flex justify-end gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? 'Creating...' : 'Create Batch'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateBatchModal;
